var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FetchError } from '@amzn/handshake-painter-typescript-client/v1';
import { HandshakePainterServiceApiV1 } from 'src/api/handshakeApi';
import { generatePath, useLocation, useNavigate, useParams } from 'react-router';
import { useDefaultApiParams } from 'src/hooks';
import { routes } from 'src/utils/routes';
import { useAnalyticsContext } from 'src/common/metrics';
/**
 * Use cases:
 *   1. Initial PDP loading (when cache is empty) which will fetch new data
 *   2. When navigating from one ASIN to another (when cache already has data)
 *     - Via product variation which will remove old data, prefetch new data, and then read it from the cache here
 *     - Via product widget which will fetch new data
 */
export const useProductDetails = (options) => {
    var _a, _b;
    const { asin } = useParams();
    const { getDefaultParams } = useDefaultApiParams();
    const location = useLocation();
    const queryClient = useQueryClient();
    const prefetchedQuery = queryClient.getQueryData(['getProductDetails', asin]);
    // We check if the query was prefetched and has data otherwise it will trigger a background refetch since we have the staleTime set to 0
    const enabled = ((_a = options === null || options === void 0 ? void 0 : options.enabled) !== null && _a !== void 0 ? _a : true) && !(((_b = location.state) === null || _b === void 0 ? void 0 : _b.prefetch) && prefetchedQuery !== undefined);
    return useQuery({
        queryKey: ['getProductDetails', asin],
        queryFn: () => __awaiter(void 0, void 0, void 0, function* () {
            const defaultApiParams = getDefaultParams();
            return yield HandshakePainterServiceApiV1.getProductDetails({
                asin: asin,
                xJoinId: defaultApiParams.xJoinId,
                xUserLinked: defaultApiParams.xUserLinked,
                marketplaceId: defaultApiParams.marketplaceId,
            });
        }),
        enabled: !!asin && enabled,
    });
};
/**
 * Use cases
 *   - Prefetching data when selecting a product variation on the PDP
 */
export const usePrefetchProductDetails = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const rumClient = useAnalyticsContext();
    const { getDefaultParams } = useDefaultApiParams();
    const abortControllerRef = React.useRef(null);
    return useMutation({
        mutationFn: (asin) => __awaiter(void 0, void 0, void 0, function* () {
            abortControllerRef.current = new AbortController();
            const defaultParams = getDefaultParams();
            return yield HandshakePainterServiceApiV1.getProductDetails({
                asin,
                xJoinId: defaultParams.xJoinId,
                xUserLinked: defaultParams.xUserLinked,
                marketplaceId: defaultParams.marketplaceId,
            }, { signal: abortControllerRef.current.signal });
        }),
        onMutate: () => __awaiter(void 0, void 0, void 0, function* () {
            var _a;
            // Cancel the previous queries
            (_a = abortControllerRef.current) === null || _a === void 0 ? void 0 : _a.abort();
            queryClient.removeQueries({ predicate: (query) => query.queryKey.includes('getProductDetails') });
        }),
        onSuccess: (response, asin) => {
            // Set the data to the cache so that the isLoading won't trigger when navigating to the new ASIN page
            queryClient.setQueryData(['getProductDetails', asin], response);
            const dpRoute = generatePath(routes.productDetailPage, { asin });
            navigate(`${dpRoute}${location.search}`, { state: { preventScrollReset: true, prefetch: true } });
        },
        onError: (error) => {
            var _a;
            if (error instanceof FetchError && error.cause.name == 'AbortError') {
                // Ignore intentional abort of API call
            }
            else {
                rumClient.recordEvent('PRODUCT_VARIATION_API_ERROR', {
                    errorTitle: error === null || error === void 0 ? void 0 : error.title,
                    errorDetails: (_a = error === null || error === void 0 ? void 0 : error.detail) !== null && _a !== void 0 ? _a : String(error),
                });
            }
        },
    });
};
